/**
 * The button, on the restyle's three states and nothing more.
 *
 * Both concept boards draw exactly three: one filled PRIMARY in the app's red, one SECONDARY, and
 * a GHOST that is a word rather than a control. That is why `outline` here is the same thing as
 * `secondary` — the boards have one, the app's call sites had two, and giving them different
 * treatments would invent a fourth state neither sheet decided.
 *
 * What the two looks disagree about is entirely in `--secondary` and `--secondary-line`: Bouquet's
 * secondary is a pink-tint fill with red-panel-deep type, Tuxedo's is a transparent-reading white
 * (or card) under a grey hairline. One class list, two rooms.
 *
 * 44px is the default height and 38 the small one — the boards' own, and a thumb's.
 */
import { Text, TextClassContext } from '@/components/ui/text';
import { useLinkInk } from '@/lib/edges';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Platform, Pressable, View } from 'react-native';

const buttonVariants = cva(
  cn(
    'group shrink-0 flex-row items-center justify-center gap-2 rounded-md shadow-none',
    Platform.select({
      web: "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    }),
  ),
  {
    variants: {
      variant: {
        default: cn('bg-primary active:bg-primary/90', Platform.select({ web: 'hover:bg-primary/90' })),
        destructive: cn('bg-destructive active:bg-destructive/90', Platform.select({ web: 'hover:bg-destructive/90' })),
        /**
         * The same RESTING look as `secondary` — see the note at the top of the file — and a
         * different answer to a pointer. `hover:bg-secondary/80` was the fill a CHOSEN thing
         * wears, so a hovered "Not my thing" read as an already-picked one. A hover is a
         * hairline going darker over a wash, which says "pressable" without saying "picked".
         */
        outline: cn(
          'bg-secondary border-secondary-line active:bg-secondary/80 border',
          Platform.select({ web: 'hover:border-line-strong hover:bg-accent/40' }),
        ),
        secondary: cn(
          'bg-secondary border-secondary-line active:bg-secondary/80 border',
          Platform.select({ web: 'hover:bg-secondary/80' }),
        ),
        // No border and no fill: a ghost is a word you can press, and both sheets draw it as one.
        ghost: cn('active:opacity-70', Platform.select({ web: 'hover:opacity-80' })),
        link: '',
      },
      size: {
        default: cn('h-11 px-4 py-2', Platform.select({ web: 'has-[>svg]:px-3' })),
        sm: cn('h-[38px] gap-1.5 rounded-md px-4', Platform.select({ web: 'has-[>svg]:px-2.5' })),
        lg: cn('h-11 rounded-md px-6', Platform.select({ web: 'has-[>svg]:px-4' })),
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/** 15px at 600 on every button, both looks — the boards set `.btn` once and never per variant. */
const buttonTextVariants = cva(
  cn('text-foreground text-body font-semibold', Platform.select({ web: 'pointer-events-none transition-colors' })),
  {
    variants: {
      variant: {
        default: 'text-primary-foreground',
        destructive: 'text-destructive-foreground',
        outline: 'text-secondary-foreground',
        secondary: 'text-secondary-foreground',
        ghost: 'text-primary group-active:text-accent-foreground',
        link: cn(
          'text-primary group-active:underline',
          Platform.select({ web: 'underline-offset-4 hover:underline group-hover:underline' }),
        ),
      },
      size: {
        default: '',
        sm: '',
        lg: '',
        icon: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * DISABLED IS A TOKEN, NOT AN OPACITY.
 *
 * `opacity-50` over a filled variant keeps the fill's HUE and only fades it, which is how a
 * disabled primary ended up wearing the same pale pink as an enabled secondary standing beside
 * it, with a white label on it at about 2:1. Off is a different surface, not a fainter one: the
 * fill becomes `--muted` and the ink `--muted-foreground`, a pair that clears 5:1 in all four
 * theme blocks. A ghost has no surface to change, so it only loses its ink.
 */
const buttonDisabledVariants = cva('', {
  variants: {
    variant: {
      default: 'bg-muted border-transparent',
      destructive: 'bg-muted border-transparent',
      outline: 'bg-muted border-transparent',
      secondary: 'bg-muted border-transparent',
      ghost: '',
      link: '',
    },
  },
  defaultVariants: { variant: 'default' },
});

/**
 * A flush ghost's negative margin, per size — always exactly the size's own horizontal padding,
 * so the LABEL lands on the text grid while the pressable box keeps its full width and height.
 */
const buttonFlushVariants = cva('', {
  variants: {
    size: { default: '-ml-4', sm: '-ml-4', lg: '-ml-6', icon: '' },
  },
  defaultVariants: { size: 'default' },
});

type ButtonProps = React.ComponentProps<typeof Pressable> &
  React.RefAttributes<typeof Pressable> &
  VariantProps<typeof buttonVariants> & {
    /**
     * Why this button cannot be pressed yet, in three or four words, drawn under it while it is
     * disabled. A control that is off and says nothing is a puzzle; the sentence is the whole
     * difference between "broken" and "not yet".
     */
    reason?: string;
    /**
     * Pull a ghost's label out to its container's own left edge. On by default, because a ghost
     * is a WORD and a word belongs on the text grid beside the paragraph it answers — its 16px
     * of invisible padding was pushing "Close" a quarter-inch right of everything above it.
     *
     * Turn it OFF for a ghost that follows something else in a row: there the negative margin
     * would slide its transparent box back over its neighbour's edge and steal presses from it.
     */
    flush?: boolean;
  };

function Button({ className, variant, size, reason, flush, ...props }: ButtonProps) {
  const flushed = variant === 'ghost' && (flush ?? true);
  /**
   * A GHOST IS A WORD, AND A WORD HAS TO BE READABLE.
   *
   * `buttonTextVariants` draws both wordy variants in --primary, which is the app's red in all
   * four theme blocks: 5.99:1 on a light card and 2.76:1 on a dark one. Every ghost in the app
   * read as disabled text at night — "Change my name", "Switch", "Close" — so the dark rooms take
   * the one accent ink that clears 4.5:1 in both of them. See `lib/edges.ts`.
   */
  const linkInk = useLinkInk();
  const wordy = variant === 'ghost' || variant === 'link';
  const pressable = (
    <TextClassContext.Provider
      value={cn(
        buttonTextVariants({ variant, size }),
        wordy && !props.disabled && linkInk,
        props.disabled && 'text-muted-foreground',
      )}>
      <Pressable
        className={cn(
          buttonVariants({ variant, size }),
          flushed && buttonFlushVariants({ size }),
          props.disabled && buttonDisabledVariants({ variant }),
          className,
        )}
        role="button"
        {...props}
      />
    </TextClassContext.Provider>
  );

  if (!props.disabled || !reason) return pressable;

  /* The wrapper is a bare column with no alignment of its own: a `self-*` or a width class on the
     button still does exactly what it did, and the reason simply hangs under it. */
  return (
    <View className="gap-1.5">
      {pressable}
      <Text variant="small" className="text-muted-foreground">
        {reason}
      </Text>
    </View>
  );
}

export { Button, buttonDisabledVariants, buttonTextVariants, buttonVariants };
export type { ButtonProps };
